import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { Modal } from '../ui/Modal';
import { formatCPF, formatPhone } from '../../utils/cpfUtils';
import { UsuariosPermissoesTab } from '../../pages/gestao/usuarios/UsuariosPermissoesTab';
import { UsuariosSenhaTab } from '../../pages/gestao/usuarios/UsuariosSenhaTab';
import type { Usuario } from '../../pages/gestao/usuarios/usuarios.types';
import { Loader2 } from 'lucide-react';

const PERFIS_CRIAVEIS = [
  { value: 'ADMIN', label: 'ADMIN' },
  { value: 'REDE', label: 'REDE' },
  { value: 'ESTADUAL', label: 'ESTADUAL' },
  { value: 'MUNICIPAL', label: 'MUNICIPAL' },
  { value: 'ESCOLA', label: 'ESCOLA' },
  { value: 'PROFESSOR', label: 'PROFESSOR' },
  { value: 'RESPONSAVEL', label: 'RESPONSÁVEL' },
  { value: 'ALUNO', label: 'ALUNO' },
] as const;

const CARGOS_ESCOLA = ['DIRETOR', 'VICE_DIRETOR', 'COORDENACAO', 'SECRETARIA'] as const;

interface UsuarioEditModalProps {
  open: boolean;
  userId: string | null;
  initialData?: {
    id?: string;
    nome?: string;
    email?: string;
    telefone?: string | null;
    cpf?: string | null;
    dataNascimento?: string | null;
    perfil?: string;
    cargo?: string | null;
    escolaId?: string | null;
    municipioId?: string | null;
    estadoId?: string | null;
    redeId?: string | null;
  } | null;
  onClose: () => void;
  onUpdated?: () => void;
}

export function UsuarioEditModal({ open, userId, initialData, onClose, onUpdated }: UsuarioEditModalProps) {
  const [user, setUser] = useState<Usuario | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTab, setEditTab] = useState<'dados' | 'permissoes' | 'senha'>('dados');
  const [resending, setResending] = useState(false);
  const [newActivationLink, setNewActivationLink] = useState<string | null>(null);

  const handleResend = async (targetUserId: string) => {
    setResending(true);
    try {
      const { data } = await api.post(`/admin/usuarios/${targetUserId}/resend`);
      const link = data.activationLink || (data.user?.tokenAtivacao ? `${window.location.origin}/ativar?token=${data.user.tokenAtivacao}` : '');
      if (link) {
        setNewActivationLink(link);
      }
      toast.success(data.message || 'Novo convite gerado com sucesso!');
      if (user) {
        setUser({
          ...user,
          tokenAtivacao: data.user?.tokenAtivacao || data.tokenAtivacao,
          tokenExpiraEm: data.user?.tokenExpiraEm || data.tokenExpiraEm,
          statusConta: 'PENDENTE',
        } as any);
      }
      onUpdated?.();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error?.message || 'Erro ao gerar novo link', err);
    } finally {
      setResending(false);
    }
  };

  // Listas de hierarquia
  const [redes, setRedes] = useState<any[]>([]);
  const [estados, setEstados] = useState<any[]>([]);
  const [municipios, setMunicipios] = useState<any[]>([]);
  const [escolas, setEscolas] = useState<any[]>([]);

  // Formulário
  const [form, setForm] = useState({
    nome: '',
    email: '',
    cpf: '',
    dataNascimento: '',
    telefone: '',
    perfil: 'ESCOLA',
    cargo: 'DIRETOR',
    redeId: '',
    estadoId: '',
    municipioId: '',
    escolaId: '',
  });

  // Carrega listas e dados completos usando as rotas canônicas (/admin/redes, /admin/estados, /admin/municipios, /escolas)
  useEffect(() => {
    if (!open) return;
    setEditTab('dados');
    setLoading(true);

    // Inicialização imediata com initialData para garantir fidelidade total sem flash
    if (initialData) {
      const initPerfil = initialData.perfil || 'ESCOLA';
      const initCargo = initialData.cargo || (initPerfil === 'PROFESSOR' ? 'PROFESSOR' : (initPerfil === 'ESCOLA' ? 'DIRETOR' : initPerfil));
      setUser({ ...initialData, perfil: initPerfil, cargo: initCargo } as any);
      setForm({
        nome: initialData.nome || '',
        email: initialData.email || '',
        cpf: initialData.cpf ? formatCPF(initialData.cpf) : '',
        dataNascimento: initialData.dataNascimento ? String(initialData.dataNascimento).slice(0, 10) : '',
        telefone: initialData.telefone ? formatPhone(initialData.telefone) : '',
        perfil: initPerfil,
        cargo: initCargo,
        redeId: initialData.redeId || '',
        estadoId: initialData.estadoId || '',
        municipioId: initialData.municipioId || '',
        escolaId: initialData.escolaId || '',
      });
    }

    const fetchList = (url: string) =>
      api.get(url)
        .then(r => {
          if (Array.isArray(r.data)) return r.data;
          if (Array.isArray(r.data?.data)) return r.data.data;
          if (Array.isArray(r.data?.items)) return r.data.items;
          return [];
        })
        .catch(() => []);

    Promise.all([
      fetchList('/admin/redes'),
      fetchList('/admin/estados'),
      fetchList('/admin/municipios'),
      fetchList('/escolas'),
      userId
        ? api.get(`/admin/usuarios/${userId}`)
            .then(r => r.data?.data || r.data)
            .catch(() => null)  // null = sem acesso (ex: role ESCOLA) — usa initialData
        : Promise.resolve(null),
    ])
      .then(([redesList, estadosList, municipiosList, escolasList, userData]) => {
        setRedes(redesList);
        setEstados(estadosList);
        setMunicipios(municipiosList);
        setEscolas(escolasList);

        const u: any = { ...(initialData || {}), ...(userData || {}) };
        const finalPerfil = userData?.perfil || initialData?.perfil || u.perfil || 'ESCOLA';
        const finalCargo = userData?.cargo || initialData?.cargo || (finalPerfil === 'PROFESSOR' ? 'PROFESSOR' : (finalPerfil === 'ESCOLA' ? 'DIRETOR' : finalPerfil));

        u.cargo = finalCargo;
        u.perfil = finalPerfil;
        setUser(u);

        let resolvedEscolaId = u.escolaId || '';
        let resolvedMunicipioId = u.municipioId || '';
        let resolvedEstadoId = u.estadoId || '';
        let resolvedRedeId = u.redeId || '';

        // Auto-resolve hierarquia completa para cima a partir da escola vinculada
        if (resolvedEscolaId) {
          const esc = escolasList.find((e: any) => e.id === resolvedEscolaId);
          if (esc) {
            resolvedMunicipioId = esc.municipioId || esc.municipio?.id || resolvedMunicipioId;
          }
        }

        if (resolvedMunicipioId) {
          const mun = municipiosList.find((m: any) => m.id === resolvedMunicipioId);
          if (mun) {
            resolvedEstadoId = mun.estadoId || mun.estado?.id || resolvedEstadoId;
          }
        }

        if (resolvedEstadoId) {
          const est = estadosList.find((e: any) => e.id === resolvedEstadoId);
          if (est) {
            resolvedRedeId = est.redeId || est.rede?.id || resolvedRedeId;
          }
        }

        // Se a rede ainda não foi resolvida, busca pelo estado ou primeira rede ativa
        if (!resolvedRedeId && redesList.length > 0) {
          resolvedRedeId = redesList[0].id;
        }

        setForm({
          nome: u.nome || '',
          email: u.email || '',
          cpf: u.cpf ? formatCPF(u.cpf) : '',
          dataNascimento: u.dataNascimento ? String(u.dataNascimento).slice(0, 10) : '',
          telefone: u.telefone ? formatPhone(u.telefone) : '',
          perfil: finalPerfil,
          cargo: finalCargo,
          redeId: resolvedRedeId,
          estadoId: resolvedEstadoId,
          municipioId: resolvedMunicipioId,
          escolaId: resolvedEscolaId,
        });
      })
      .finally(() => setLoading(false));
  }, [open, userId, initialData]);

  const handlePerfilChange = (newPerfil: string) => {
    setForm(f => {
      const updated = { ...f, perfil: newPerfil };
      if (newPerfil === 'ADMIN') {
        updated.redeId = '';
        updated.estadoId = '';
        updated.municipioId = '';
        updated.escolaId = '';
        updated.cargo = 'DIRETOR';
      } else if (newPerfil === 'REDE') {
        updated.estadoId = '';
        updated.municipioId = '';
        updated.escolaId = '';
        updated.cargo = 'DIRETOR';
      } else if (newPerfil === 'ESTADUAL' || newPerfil === 'ESTADO') {
        updated.municipioId = '';
        updated.escolaId = '';
        updated.cargo = 'DIRETOR';
      } else if (newPerfil === 'MUNICIPAL') {
        updated.escolaId = '';
        updated.cargo = 'DIRETOR';
      } else if (newPerfil !== 'ESCOLA') {
        updated.cargo = 'DIRETOR';
      }
      return updated;
    });
  };

  // Listas filtradas com inclusão segura do item atualmente selecionado
  const estadosFiltrados = useMemo(() => {
    if (!form.redeId) return estados;
    return estados.filter(e => e.redeId === form.redeId || e.id === form.estadoId);
  }, [estados, form.redeId, form.estadoId]);

  const municipiosFiltrados = useMemo(() => {
    if (!form.estadoId) return municipios;
    return municipios.filter(m => m.estadoId === form.estadoId || m.id === form.municipioId);
  }, [municipios, form.estadoId, form.municipioId]);

  const escolasFiltradas = useMemo(() => {
    if (!form.municipioId) return escolas;
    return escolas.filter(e => e.municipioId === form.municipioId || e.id === form.escolaId);
  }, [escolas, form.municipioId, form.escolaId]);

  const handleSave = async () => {
    if (!form.nome.trim() || !form.email.trim()) {
      toast.warning('Nome e E-mail são obrigatórios');
      return;
    }

    setSaving(true);
    try {
      const targetId = userId || user?.id;
      if (!targetId) throw new Error('ID do usuário não encontrado');

      const isSchoolScoped = ['ESCOLA', 'PROFESSOR', 'RESPONSAVEL', 'ALUNO'].includes(form.perfil?.toUpperCase());

      await api.put(`/admin/usuarios/${targetId}`, {
        nome: form.nome.trim(),
        email: form.email.trim(),
        perfil: form.perfil,
        cargo: form.perfil === 'ESCOLA' ? form.cargo : (form.perfil === 'PROFESSOR' ? 'PROFESSOR' : null),
        telefone: form.telefone ? form.telefone.replace(/\D/g, '') : null,
        cpf: form.cpf ? form.cpf.replace(/\D/g, '') : null,
        dataNascimento: form.dataNascimento ? new Date(form.dataNascimento).toISOString() : null,
        redeId: form.redeId || null,
        estadoId: form.estadoId || null,
        municipioId: form.municipioId || null,
        escolaId: isSchoolScoped ? (form.escolaId || null) : (form.escolaId || null),
      });

      toast.success('Usuário atualizado com sucesso!');
      onUpdated?.();
      onClose();
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Erro ao atualizar usuário';
      const lower = errorMsg.toLowerCase();
      if (lower.includes('cpf') || lower.includes('e-mail') || lower.includes('email') || lower.includes('já existe') || lower.includes('já está cadastrado') || err?.response?.status === 409 || err?.response?.status === 400) {
        toast.warning(`Conflito de Dados: ${errorMsg}`);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title=""
      size="lg"
      contentClassName="umu-wizard-modal"
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <button type="button" className="btn-cancel" onClick={onClose}>Cancelar</button>
          {editTab === 'dados' && (
            <button type="button" className="btn-save" onClick={handleSave} disabled={saving || loading}>
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          )}
        </div>
      }
    >
      {/* Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 40%, #3730a3 100%)',
        borderRadius: '12px 12px 0 0', padding: '20px 24px 16px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(99,102,241,.12)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 800, margin: '0 0 4px' }}>
            Editar Usuário
          </h2>
          <p style={{ color: 'rgba(255,255,255,.55)', fontSize: '.78rem', margin: 0 }}>
            Atualize os dados do usuário.
          </p>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '20px 24px', flex: 1, overflowY: 'auto' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border, #e5e7eb)', marginBottom: 16 }}>
          {[
            { key: 'dados' as const, label: 'Dados' },
            ...(['ESCOLA', 'PROFESSOR'].includes(form.perfil?.toUpperCase()) ? [{ key: 'permissoes' as const, label: 'Permissões' }] : []),
            { key: 'senha' as const, label: 'Senha' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setEditTab(tab.key)}
              style={{
                padding: '8px 16px', fontSize: '.8rem', fontWeight: 600,
                border: 'none', cursor: 'pointer', transition: 'all .15s',
                background: 'transparent',
                color: editTab === tab.key ? '#6366f1' : '#6b7280',
                borderBottom: editTab === tab.key ? '2px solid #6366f1' : '2px solid transparent',
                marginBottom: '-2px',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Conteúdo */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40, color: '#94a3b8' }}>
            <Loader2 className="animate-spin" size={28} />
          </div>
        ) : (
          <div>
            {/* Tab: Permissões */}
            {editTab === 'permissoes' && user && (
              <UsuariosPermissoesTab user={user} onUpdated={onUpdated} />
            )}

            {/* Tab: Senha */}
            {editTab === 'senha' && user && (
              <UsuariosSenhaTab
                user={user}
                onUpdated={onUpdated}
                onResend={handleResend}
                resending={resending}
                newActivationLink={newActivationLink}
              />
            )}

            {/* Tab: Dados */}
            {editTab === 'dados' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Row 1: Nome e Email */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Nome completo *</label>
                    <input
                      className="form-input"
                      value={form.nome}
                      onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                      placeholder="Nome do usuário"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Email institucional *</label>
                    <input
                      className="form-input"
                      type="email"
                      value={form.email}
                      disabled
                      style={{ background: '#f1f5f9', color: '#64748b' }}
                    />
                  </div>
                </div>

                {/* Row 2: CPF e Data de Nascimento */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">CPF (Opcional)</label>
                    <input
                      className="form-input"
                      value={form.cpf}
                      onChange={e => setForm(f => ({ ...f, cpf: formatCPF(e.target.value) }))}
                      placeholder="000.000.000-00"
                      maxLength={14}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Data de Nascimento (Opcional)</label>
                    <input
                      className="form-input"
                      type="date"
                      value={form.dataNascimento}
                      onChange={e => setForm(f => ({ ...f, dataNascimento: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Row 3: Telefone */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Telefone / WhatsApp (Celular) *</label>
                  <input
                    className="form-input"
                    value={form.telefone}
                    onChange={e => setForm(f => ({ ...f, telefone: formatPhone(e.target.value) }))}
                    placeholder="(00) 00000-0000"
                  />
                </div>

                {/* Row 4: Perfil e Cargo */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Perfil *</label>
                    <select
                      className="form-select"
                      value={form.perfil}
                      onChange={e => handlePerfilChange(e.target.value)}
                    >
                      {PERFIS_CRIAVEIS.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group" style={{ marginBottom: 0, opacity: form.perfil?.toUpperCase() !== 'ESCOLA' ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                    <label className="form-label">Cargo na Escola {form.perfil?.toUpperCase() === 'ESCOLA' && '*'}</label>
                    <select
                      className="form-select"
                      value={form.perfil?.toUpperCase() === 'ESCOLA' ? form.cargo : ''}
                      disabled={form.perfil?.toUpperCase() !== 'ESCOLA'}
                      onChange={e => setForm(f => ({ ...f, cargo: e.target.value }))}
                    >
                      {form.perfil?.toUpperCase() !== 'ESCOLA' && <option value="">Não aplicável</option>}
                      {CARGOS_ESCOLA.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row 5: Hierarquia Oficial em Linha Única (4 Colunas: Rede, Estado, Município, Escola) */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14 }}>
                  {/* Rede */}
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Rede *</label>
                    <select
                      className="form-select"
                      value={form.redeId || ''}
                      onChange={e => {
                        const val = e.target.value;
                        setForm(f => ({ ...f, redeId: val, estadoId: '', municipioId: '', escolaId: '' }));
                      }}
                    >
                      <option value="">Selecione</option>
                      {redes.map(r => (
                        <option key={r.id} value={r.id}>{r.nome}</option>
                      ))}
                    </select>
                  </div>

                  {/* Estado */}
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Estado *</label>
                    <select
                      className="form-select"
                      value={form.estadoId || ''}
                      onChange={e => {
                        const val = e.target.value;
                        setForm(f => ({ ...f, estadoId: val, municipioId: '', escolaId: '' }));
                      }}
                    >
                      <option value="">Selecione</option>
                      {estadosFiltrados.map(est => (
                        <option key={est.id} value={est.id}>{est.nome} {est.sigla ? `(${est.sigla})` : ''}</option>
                      ))}
                    </select>
                  </div>

                  {/* Município */}
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Município *</label>
                    <select
                      className="form-select"
                      value={form.municipioId || ''}
                      onChange={e => {
                        const val = e.target.value;
                        setForm(f => ({ ...f, municipioId: val, escolaId: '' }));
                      }}
                    >
                      <option value="">Selecione</option>
                      {municipiosFiltrados.map(m => (
                        <option key={m.id} value={m.id}>{m.nome}</option>
                      ))}
                    </select>
                  </div>

                  {/* Escola */}
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Escola *</label>
                    <select
                      className="form-select"
                      value={form.escolaId || ''}
                      onChange={e => {
                        const val = e.target.value;
                        setForm(f => ({ ...f, escolaId: val }));
                      }}
                    >
                      <option value="">Selecione</option>
                      {escolasFiltradas.map(esc => (
                        <option key={esc.id} value={esc.id}>{esc.nome}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
